import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, RefreshCw, Upload } from 'lucide-react';
import api from '@/lib/api';
import { erroDaApi } from '@/lib/erroDaApi';
import TelaProcessando from '@/components/TelaProcessando';
import { CABECALHO, CELULA, LINHA, TITULO_COLUNA } from '../cotacoes/tabela';
import type { ConferenciaDoDePara, PreviaDosItens } from './tipos';

const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/**
 * A tabela de itens do Compumate: o cadastro inteiro, 1.230 códigos.
 *
 * Dois passos como na importação da cotação — o sistema mostra o que entendeu
 * e só grava depois de conferido. A planilha é gerada de novo toda semana, e o
 * que importa ver antes é quantos códigos são novos e quantos já existiam.
 */
export default function ImportarItensErpPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<PreviaDosItens | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [processando, setProcessando] = useState<'lendo' | 'gravando' | null>(null);

  const enviar = (caminho: string, file: File) => {
    const dados = new FormData();
    dados.append('arquivo', file);

    return api.post<{ data: PreviaDosItens }>(caminho, dados);
  };

  const analisar = useMutation({
    mutationFn: async (file: File) => (await enviar('/itens-erp/previa', file)).data.data,
    onSuccess: (dados) => {
      setProcessando(null);
      setPrevia(dados);
      setErro(null);
    },
    onError: (falha) => {
      setProcessando(null);
      setPrevia(null);
      setErro(erroDaApi(falha, 'Não foi possível ler a planilha.'));
    },
  });

  const importar = useMutation({
    mutationFn: async (file: File) => (await enviar('/itens-erp', file)).data.data,
    onSuccess: () => {
      setProcessando(null);
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      navigate('/produtos');
    },
    onError: (falha) => {
      setProcessando(null);
      setErro(erroDaApi(falha, 'Não foi possível gravar o cadastro.'));
    },
  });

  const escolher = (file: File | null) => {
    if (!file) {
      return;
    }

    setArquivo(file);
    setPrevia(null);
    setErro(null);
    setProcessando('lendo');
    analisar.mutate(file);
  };

  return (
    <div className="px-lg py-lg">
      {processando === 'lendo' && (
        <TelaProcessando
          titulo="Lendo a tabela de itens…"
          detalhe="Separando marca, cor e embalagem de cada código. Não feche esta janela."
        />
      )}

      {processando === 'gravando' && (
        <TelaProcessando
          titulo="Gravando o cadastro…"
          detalhe="São mais de mil códigos; o de-para já aprendido continua onde está."
        />
      )}

      <Link to="/produtos" className="mb-sm inline-flex items-center gap-xs text-[13px] text-muted hover:text-accent">
        <ArrowLeft size={14} aria-hidden="true" />
        Produtos
      </Link>

      <div className="mb-lg flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0">
          <h1 className="text-[18px] font-bold text-ink">Importar tabela de itens</h1>
          <p className="text-[13px] text-muted">
            O cadastro inteiro do Compumate, um código por cor e por marca. Código que já existe é atualizado; o que não
            vier na planilha fica como está.
          </p>
        </div>

        {previa && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex shrink-0 items-center gap-xs rounded-md border border-border bg-surface px-md py-sm text-[13px] font-bold text-ink hover:border-accent"
          >
            <RefreshCw size={15} aria-hidden="true" />
            Trocar arquivo
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx"
        className="sr-only"
        onChange={(evento) => {
          escolher(evento.target.files?.[0] ?? null);
          evento.target.value = '';
        }}
      />

      {!previa && (
        <div className="rounded-lg border border-border bg-surface p-lg">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex w-full items-center gap-md rounded-lg border-2 border-dashed border-border px-lg py-lg text-left hover:border-accent"
          >
            <Upload size={20} className="shrink-0 text-muted" aria-hidden="true" />
            <span>
              <span className="block text-[14px] font-bold text-ink">
                {arquivo ? arquivo.name : 'Escolher a tabela de itens exportada do ERP'}
              </span>
              <span className="block text-[12px] text-muted">
                {arquivo ? 'Clique para trocar o arquivo' : 'Arquivo .xls ou .xlsx'}
              </span>
            </span>
          </button>

          {erro && (
            <p className="mt-md flex items-start gap-sm rounded-md border border-danger/30 bg-danger/5 px-md py-sm text-[13px] text-danger">
              <AlertCircle size={15} className="mt-[2px] shrink-0" aria-hidden="true" />
              {erro}
            </p>
          )}
        </div>
      )}

      {previa && (
        <>
          <div className="mb-md grid gap-md sm:grid-cols-2 lg:grid-cols-5">
            <Numero titulo="Itens na planilha" valor={previa.linhas_lidas} />
            <Numero titulo="Códigos novos" valor={previa.novos} destaque />
            <Numero titulo="Já cadastrados" valor={previa.atualizados} />
            <Numero titulo="Fora de linha" valor={previa.inativos} />
            <Numero titulo="Produtos novos" valor={previa.produtos_novos} />
          </div>

          {previa.sem_produto > 0 && (
            <p className="mb-md rounded-md border border-warning/30 bg-warning/5 px-md py-sm text-[13px] text-ink-soft">
              {previa.sem_produto === 1
                ? '1 código não vira produto de cotação — é cabo de alarme, de rede ou parecido, que não se cota por bitola. Ele entra no cadastro assim mesmo.'
                : `${inteiro.format(previa.sem_produto)} códigos não viram produto de cotação — são cabos de alarme, de rede e parecidos, que não se cotam por bitola. Eles entram no cadastro assim mesmo.`}
            </p>
          )}

          <DePara conferencia={previa.de_para} />

          <div className="mb-md flex flex-wrap gap-xs">
            {Object.entries(previa.por_marca).map(([marca, quantos]) => (
              <span
                key={marca}
                className="rounded-md border border-border bg-surface px-sm py-xs text-[12px] text-ink-soft"
              >
                {marca}: <span className="tabular-nums font-bold">{inteiro.format(quantos)}</span>
              </span>
            ))}
          </div>

          <p className="mb-sm text-[12px] text-muted">As {previa.amostra.length} primeiras linhas, como o sistema as entendeu:</p>

          <div className="overflow-x-auto rounded-lg border border-border bg-surface">
            <table className="w-full text-[13px]">
              <thead>
                <tr className={CABECALHO}>
                  <th className={`${TITULO_COLUNA} text-left`}>Código</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Descrição no ERP</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Produto da cotação</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Marca</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Cor</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Embalagem</th>
                </tr>
              </thead>
              <tbody>
                {previa.amostra.map((linha) => (
                  <tr key={linha.codigo} className={`${LINHA} ${linha.ativo ? '' : 'text-muted'}`}>
                    <td className={`${CELULA} tabular-nums font-bold`}>{linha.codigo}</td>
                    <td className={CELULA}>{linha.descricao}</td>
                    <td className={CELULA}>{linha.produto ?? <span className="text-warning">sem produto</span>}</td>
                    <td className={CELULA}>{linha.marca ?? '—'}</td>
                    <td className={CELULA}>{linha.cor ?? '—'}</td>
                    <td className={CELULA}>
                      {linha.embalagem ?? (linha.unidade ? `Por ${linha.unidade.toLowerCase()}` : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {erro && (
            <p className="mt-md flex items-start gap-sm rounded-md border border-danger/30 bg-danger/5 px-md py-sm text-[13px] text-danger">
              <AlertCircle size={15} className="mt-[2px] shrink-0" aria-hidden="true" />
              {erro}
            </p>
          )}

          <div className="mt-lg flex flex-wrap items-center gap-md">
            <button
              type="button"
              disabled={!arquivo || importar.isPending}
              onClick={() => {
                if (arquivo) {
                  setProcessando('gravando');
                  importar.mutate(arquivo);
                }
              }}
              className="inline-flex items-center gap-xs rounded-md bg-faixa px-lg py-sm text-[14px] font-bold text-white disabled:opacity-40"
            >
              Importar {inteiro.format(previa.linhas_lidas)} itens
            </button>
            <Link to="/produtos" className="text-[13px] text-muted hover:text-accent">
              Cancelar
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * O que a planilha diz do de-para da compra.
 *
 * Só aparece quando a exportação traz a coluna "Codigo Produto Fornec" — a Base
 * Geral traz, a Tabela de itens não. O que o comprador precisa ver aqui não é
 * que deu tudo certo: é o que discorda do orçamento e o que o Compumate tem
 * preenchido errado.
 */
function DePara({ conferencia }: { conferencia: ConferenciaDoDePara }) {
  if (conferencia.com_codigo === 0) {
    return null;
  }

  const { divergem, codigo_da_casa: daCasa } = conferencia;

  return (
    <div className="mb-md rounded-lg border border-border bg-surface p-md">
      <div className="mb-sm flex flex-wrap items-baseline gap-sm">
        <h2 className="text-[14px] font-bold text-ink">Código do fornecedor</h2>
        <p className="text-[12px] text-muted">
          {inteiro.format(conferencia.com_codigo)} itens trazem o código do fabricante. O código do orçamento continua
          valendo; o cadastro entra para conferir e para completar o que falta.
        </p>
      </div>

      <div className="grid gap-sm sm:grid-cols-2 lg:grid-cols-4">
        <Contagem rotulo="Ganham código" valor={conferencia.novos} tom="accent" />
        <Contagem rotulo="Conferem com o orçamento" valor={conferencia.conferem} />
        <Contagem rotulo="Completam a cor ou a embalagem" valor={conferencia.completam} />
        <Contagem rotulo="Discordam do orçamento" valor={divergem.length} tom={divergem.length > 0 ? 'warning' : undefined} />
      </div>

      {daCasa.length > 0 && (
        <p className="mt-md rounded-md border border-warning/30 bg-warning/5 px-md py-sm text-[13px] text-ink-soft">
          {inteiro.format(daCasa.length)}{' '}
          {daCasa.length === 1 ? 'item tem, no campo' : 'itens têm, no campo'} do código do fornecedor, o código da
          própria Condusvale — {daCasa.slice(0, 4).map((linha) => linha.codigo).join(', ')}
          {daCasa.length > 4 ? ' e outros' : ''}. Esses ficam de fora: pedir por eles seria pedir um código que só
          existe aqui dentro.
        </p>
      )}

      {conferencia.sem_fornecedor > 0 && (
        <p className="mt-sm text-[12px] text-muted">
          {inteiro.format(conferencia.sem_fornecedor)} códigos são de marcas sem fornecedor cadastrado e ficam de fora.
        </p>
      )}

      {divergem.length > 0 && (
        <div className="mt-md">
          <p className="mb-xs text-[12px] text-muted">
            O cadastro e o orçamento discordam nestes — vale conferir qual dos dois está velho:
          </p>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-[13px]">
              <thead>
                <tr className={CABECALHO}>
                  <th className={`${TITULO_COLUNA} text-left`}>Código</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Descrição no ERP</th>
                  <th className={`${TITULO_COLUNA} text-left`}>No orçamento</th>
                  <th className={`${TITULO_COLUNA} text-left`}>No cadastro</th>
                </tr>
              </thead>
              <tbody>
                {divergem.slice(0, 12).map((linha) => (
                  <tr key={linha.codigo} className={LINHA}>
                    <td className={`${CELULA} tabular-nums font-bold`}>{linha.codigo}</td>
                    <td className={CELULA}>{linha.descricao}</td>
                    <td className={`${CELULA} font-bold text-ink`}>{linha.orcamento ?? '—'}</td>
                    <td className={`${CELULA} text-muted`}>{linha.cadastro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {divergem.length > 12 && (
            <p className="mt-xs text-[12px] text-muted">e mais {inteiro.format(divergem.length - 12)}.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Contagem({ rotulo, valor, tom }: { rotulo: string; valor: number; tom?: 'accent' | 'warning' }) {
  const cor = tom === 'accent' ? 'text-accent' : tom === 'warning' ? 'text-warning' : 'text-ink';

  return (
    <div className="rounded-md border border-border px-sm py-xs">
      <p className={`text-[17px] font-bold tabular-nums ${cor}`}>{inteiro.format(valor)}</p>
      <p className="text-[12px] text-muted">{rotulo}</p>
    </div>
  );
}

function Numero({ titulo, valor, destaque = false }: { titulo: string; valor: number; destaque?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-md py-sm">
      <p className="text-[12px] text-muted">{titulo}</p>
      <p className={`text-[20px] font-bold tabular-nums ${destaque ? 'text-accent' : 'text-ink'}`}>
        {inteiro.format(valor)}
      </p>
    </div>
  );
}
